import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, MessageSquare, Pencil, Trash2, X } from 'lucide-react';
import { IconButton, Spinner, TextField } from '../ui';
import { useConversations } from '../../context/ConversationContext';

const DELETE_CONFIRM_MS = 3000;

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/**
 * Groups conversations into Heute / Gestern / Diese Woche / Älter (spec §8.2).
 * Exported for the unit test — pure, no DOM, no i18n.
 */
export function groupConversations(conversations, now = Date.now()) {
  const today = startOfDay(now);
  const groups = { today: [], yesterday: [], week: [], older: [] };

  for (const conversation of conversations ?? []) {
    const day = startOfDay(conversation.updated_at ?? conversation.created_at);
    if (day === null || day >= today) groups.today.push(conversation);
    else if (day >= today - DAY_MS) groups.yesterday.push(conversation);
    else if (day > today - 7 * DAY_MS) groups.week.push(conversation);
    else groups.older.push(conversation);
  }

  return groups;
}

const GROUP_ORDER = [
  ['today', 'chat.historyToday'],
  ['yesterday', 'chat.historyYesterday'],
  ['week', 'chat.historyThisWeek'],
  ['older', 'chat.historyOlder'],
];

/**
 * History search + grouped conversation list. Rendered inside the desktop
 * sidebar and, on tablet/mobile, inside the menu drawer — the two differ only
 * in which scroll container class they use, because the sidebar's own scroll
 * region collapses on the icon rail.
 *
 * States (spec §11): loading · empty · no-results · success. There is no error
 * state of its own — `ConversationContext.refresh()` swallows failures and
 * keeps the last good list, which then renders as "empty".
 *
 * Renaming and deleting live here because this is the only history in the app.
 * They used to sit in a second, richer history column rendered inside the chat
 * view; that column is gone (spec §8.2 puts history in the sidebar), so its
 * capabilities moved here rather than disappearing with it.
 *
 * @param {object} props
 * @param {boolean} [props.inSidebar=false]
 * @param {() => void} [props.onNavigate] - called after a conversation is
 *   opened, so the hosting drawer can close itself.
 */
export function HistoryPanel({ inSidebar = false, onNavigate }) {
  const { t } = useTranslation();
  const {
    conversations, currentId, setCurrentId, searchQuery, setSearchQuery, loading,
    renameConversation, deleteConversation,
  } = useConversations();

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const editInputRef = useRef(null);
  const confirmTimer = useRef(null);

  const groups = useMemo(() => groupConversations(conversations), [conversations]);
  const isEmpty = !conversations || conversations.length === 0;

  useEffect(() => () => clearTimeout(confirmTimer.current), []);

  useEffect(() => {
    if (editingId) editInputRef.current?.select?.();
  }, [editingId]);

  const handleSelect = (id) => {
    setCurrentId(id);
    onNavigate?.();
  };

  const startEdit = (conversation) => {
    setEditingId(conversation.id);
    setEditValue(conversation.title || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const commitEdit = async () => {
    if (!editingId) return;
    const title = editValue.trim();
    if (title) {
      try { await renameConversation(editingId, title); } catch { /* surfaced by the context */ }
    }
    cancelEdit();
  };

  // Two-step delete without a modal: the second press inside the window
  // confirms. The button's accessible name changes with the state, so the
  // confirmation is not carried by colour alone (spec §12).
  const handleDelete = async (id) => {
    if (confirmDelete !== id) {
      setConfirmDelete(id);
      clearTimeout(confirmTimer.current);
      confirmTimer.current = setTimeout(
        () => setConfirmDelete((prev) => (prev === id ? null : prev)),
        DELETE_CONFIRM_MS,
      );
      return;
    }
    try { await deleteConversation(id); } catch { /* surfaced by the context */ }
    setConfirmDelete(null);
  };

  const renderItem = (conversation) => {
    const title = conversation.title || t('chat.historyUntitled');

    if (editingId === conversation.id) {
      return (
        <li key={conversation.id} className="etch-history-item" data-editing="true">
          <TextField
            ref={editInputRef}
            label={t('chat.renameLabel')}
            hideLabel
            autoFocus
            value={editValue}
            onChange={(event) => setEditValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commitEdit();
              if (event.key === 'Escape') cancelEdit();
            }}
          />
          <IconButton label={t('chat.renameSave')} onClick={commitEdit}>
            <Check size={14} />
          </IconButton>
          <IconButton label={t('chat.renameCancel')} onClick={cancelEdit}>
            <X size={14} />
          </IconButton>
        </li>
      );
    }

    const pendingDelete = confirmDelete === conversation.id;

    return (
      <li
        key={conversation.id}
        className="etch-history-item"
        data-active={conversation.id === currentId || undefined}
      >
        <Link
          to="/chat"
          className="etch-history-item__link"
          aria-current={conversation.id === currentId ? 'true' : undefined}
          onClick={() => handleSelect(conversation.id)}
        >
          <MessageSquare size={15} aria-hidden="true" />
          <span className="etch-history-item__title">{title}</span>
        </Link>
        <span className="etch-history-item__actions">
          <IconButton label={`${t('chat.rename')}: ${title}`} onClick={() => startEdit(conversation)}>
            <Pencil size={13} />
          </IconButton>
          <IconButton
            label={pendingDelete ? t('chat.deleteConfirm') : `${t('chat.delete')}: ${title}`}
            tone="danger"
            data-pending={pendingDelete || undefined}
            onClick={() => handleDelete(conversation.id)}
          >
            <Trash2 size={13} />
          </IconButton>
        </span>
      </li>
    );
  };

  return (
    <>
      {/* The drawer variant already gets a visible heading from `Drawer`. */}
      {inSidebar && <h2 className="etch-visually-hidden">{t('nav.history')}</h2>}

      <div className={`etch-shell-search${inSidebar ? ' etch-shell-desktop-only' : ''}`}>
        <TextField
          type="search"
          label={t('nav.searchHistory')}
          hideLabel
          value={searchQuery}
          placeholder={t('chat.searchPlaceholder')}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
      </div>

      <div className={inSidebar ? 'etch-sidebar__scroll' : 'etch-shell-drawer-scroll'}>
        {loading && isEmpty ? (
          <p className="etch-shell-note">
            <Spinner />
            {t('states.loading')}
          </p>
        ) : isEmpty ? (
          <p className="etch-shell-note">{searchQuery ? t('chat.noResults') : t('chat.historyEmpty')}</p>
        ) : (
          GROUP_ORDER.map(([key, labelKey]) =>
            groups[key].length === 0 ? null : (
              <section key={key} className="etch-history-group" aria-label={t(labelKey)}>
                <h3 className="etch-eyebrow etch-shell-group-title">{t(labelKey)}</h3>
                <ul className="etch-history-list">{groups[key].map(renderItem)}</ul>
              </section>
            )
          )
        )}
      </div>
    </>
  );
}

export default HistoryPanel;
